import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Observable, BehaviorSubject, Subscription, switchMap, throwError, take, skip, fromEvent } from 'rxjs';
import { DiaryInterface } from '../../../models/interface/diary.interface';
import { InterventionInterface } from '../../../models/interface/intervention.interface';
import { LessonInterface } from '../../../models/interface/lesson.interface';
import { SkillInterface } from '../../../models/interface/skill.interface';
import { HelperDialogService } from '../../../services/helper/helper-dialog/helper-dialog.service';
import { HelperService } from '../../../services/helper/helper.service';
import { InterventionSharedService } from '../../../services/shared/intervention-shared/intervention-shared.service';
import { DiaryStore } from '../../../store/diary/component-store/diary.store';
import { QuestionnaireStore } from '../../../store/lesson-questionnaire/component-store/lesson-questionnaire.store';
import { SkillStore } from '../../../store/skill/component-store/skill.store';
import { Chart } from 'chart.js/auto';
import { InteractionItem } from 'chart.js/dist/core/core.interaction';

type SkillDataSet = {
  label: string;
  data: number[];
  backgroundColor: string[];
};

@Component({
  selector: 'app-intervention-skill',
  templateUrl: './intervention-skill.component.html',
  styleUrls: ['./intervention-skill.component.scss'],
  providers: [DiaryStore, QuestionnaireStore, SkillStore],
})
export class InterventionSkillComponent implements OnInit, OnDestroy {
  public canvas: ElementRef;

  public isLoading$: Observable<boolean>;

  public lessonSkills: Array<{ skillId: number; lessonIds: number[] }> = [];

  // Data provided by InterventionSharedService
  public studyId: number;
  public intervention: InterventionInterface;

  public lessons: LessonInterface[];

  public diaries: DiaryInterface[];

  public diariesOfStudy$: Observable<DiaryInterface[]>;

  public questionnairesOfIntervention$: Observable<LessonInterface[]>;

  public skills: SkillInterface[];

  public skillsOfIntervention$: Observable<SkillInterface[]>;

  // New Chart
  public chart: Chart<"doughnut", number[], string>;
  public dataset: { labels: string[]; datasets: SkillDataSet[] } = { labels: [], datasets: [] };

  private isLoadingSubject = new BehaviorSubject<boolean>(true);

  private subscriptions: Subscription[] = [];

  constructor(
    private sharedService: InterventionSharedService,
    private diaryStore: DiaryStore,
    private questionnaireStore: QuestionnaireStore,
    private skillStore: SkillStore,
    private helperService: HelperService,
    private helperDialogService: HelperDialogService,
  ) {
    this.isLoading$ = this.isLoadingSubject.asObservable();
    this.diariesOfStudy$ = this.diaryStore.diariesOfStudy$;
    this.questionnairesOfIntervention$ = this.questionnaireStore.questionnairesOfIntervention$;
    this.skillsOfIntervention$ = this.skillStore.skillsOfIntervention$;
  }

  public get helper() {
    return this.helperService;
  }

  public get helperDialog() {
    return this.helperDialogService;
  }

  @ViewChild('canvas', { static: false }) set content(content: ElementRef) {
    if (content) {
      this.canvas = content;
      this.chart = new Chart(this.canvas.nativeElement.getContext('2d'), {
        type: 'doughnut',
        data: this.dataset,
        options: {
          responsive: true,
          onHover: function(e) {
            const points = this.getElementsAtEventForMode(
              e,
              'point',
              { intersect: true },
              true,
            );
            if (points.length) {
              (e.native.target as HTMLElement).style.cursor = 'pointer';
            } else {
              (e.native.target as HTMLElement).style.cursor = 'default';
            }
          },
          plugins: {
            legend: {
              onHover: function(event) {
                (event.native.target as HTMLElement).style.cursor = 'pointer';
              },
            },
          },
        },
      });
      fromEvent(content.nativeElement, 'click').subscribe((event: Event) => {
        const activePoints: InteractionItem[] = this.chart.getElementsAtEventForMode(
          event,
          'point',
          { intersect: true },
          true,
        );
        // Array has no elements if label is clicked to disable/enable a skill from the chart
        if (activePoints.length > 0) {
          this.onSelectSkill(this.lessonSkills[activePoints[0].index].skillId);
        }
      });
    }
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.sharedService.intervention$
        .pipe(
          switchMap((intervention) => {
            this.studyId = intervention.attributes.study_id;
            this.intervention = intervention;
            this.lessonSkills = [];
            if (this.intervention.attributes.gamification.skills_order) {
              this.intervention.attributes.gamification.skills_order.forEach((skillId: number) => {
                this.lessonSkills.push({ skillId, lessonIds: [] });
              });
            }
            this.diaryStore.getDiariesOfStudy({ studyId: this.studyId });
            return this.diariesOfStudy$.pipe(skip(1), take(1));
          }),
          switchMap((diaries) => {
            this.diaries = diaries;
            this.questionnaireStore.getQuestionnairesIntervention({ interventionId: this.intervention.id });
            return this.questionnairesOfIntervention$.pipe(skip(1), take(1));
          }),
          switchMap((questionnaires) => {
            this.lessons = questionnaires;
            this.skillStore.getSkillsOfIntervention({ id: this.intervention.id });
            return this.skillsOfIntervention$.pipe(skip(1), take(1));
          })
        )
        .subscribe({
          next: (skills) => {
            this.skills = skills;
            this.lessons.forEach((lesson) => {
              if (lesson.attributes.skills) {
                lesson.attributes.skills.forEach((skillId) => {
                  const lessonSkill = this.lessonSkills.find(
                    (value) => value.skillId.toString() === skillId.toString(),
                  );
                  if (lessonSkill !== undefined) {
                    lessonSkill.lessonIds.push(lesson.id);
                  }
                });
              }
            });
            if (this.skills.length > 0) {
              const skillData: number[] = [];
              const skillBackgroundColors: string[] = [];
              this.lessonSkills.forEach((value) => {
                if (value.lessonIds.length > 0) {
                  const foundSkill = this.skills.find((skill) => skill.id.toString() === value.skillId.toString());
                  if (foundSkill) {
                    this.dataset.labels.push(foundSkill.attributes.title);
                    skillData.push(value.lessonIds.length);
                    const color = foundSkill.attributes.color;
                    skillBackgroundColors.push(color || '#15417E');
                  }
                }
              });
              if (skillData.length > 0) {
                const skillDataSet: SkillDataSet = {
                  label: '',
                  data: skillData,
                  backgroundColor: skillBackgroundColors,
                };
                this.dataset.datasets.push(skillDataSet);
              }
            }
            this.lessons = this.helper.sortLessonsAscending(this.lessons);
            this.isLoadingSubject.next(false);
          },
          error: (error) => {
            throwError(() => error);
          },
        })
    );
  }

  public onSelectSkill(skillId: number): void {
    const dialogRef = this.helperDialog.openDialogSkillByName(
      skillId,
      this.skills,
      undefined,
      this.intervention,
    );
    if (dialogRef) {
      dialogRef.afterClosed().subscribe(() => {});
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
